import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { StatusDot } from "@/components/brand/status-dot";
import type { InterviewSession, ToolEvent } from "../../ports.ts";
import type { BuilderConfig, InterviewStart } from "../api.ts";
import { useNav } from "../nav.tsx";
import { createSerialQueue } from "../serial-queue.ts";
import { ErrorNote, errText, formatWhen, SectionTitle } from "../ui.tsx";
import type { StageProps } from "./EmployeeShell.tsx";

/** The subset of the studio SDK's DelegateAvatar this screen uses. */
type StudioAvatar = {
  connectOpenAI(o: { ephemeralToken: string; model?: string; onToolCall?: (name: string, args: unknown) => Promise<string>; onEvent?: (m: any) => void }): Promise<void>;
  disconnect(): void;
  dispose(): void;
  channel?: RTCDataChannel | null;
  onStatus?: (s: string) => void;
};

type LiveTurn = { role: "user" | "avatar"; text: string };

export function InterviewStage({ api, detail, reload }: StageProps) {
  const { go } = useNav();
  const e = detail.employee;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const avatarRef = useRef<StudioAvatar | null>(null);
  const liveRef = useRef<InterviewStart | null>(null);
  // Turns are saved strictly in the order they were heard, and ending waits
  // for the last one, so distillation reads the whole conversation in order.
  const turnQueue = useRef(createSerialQueue(() => setError("A turn could not be saved. The interview continues; reload to check."))).current;
  const [config, setConfig] = useState<BuilderConfig>();
  const [avatarState, setAvatarState] = useState<"loading" | "ready" | "failed">("loading");
  const [status, setStatus] = useState("");
  const [live, setLive] = useState<InterviewStart | null>(null);
  const [turns, setTurns] = useState<LiveTurn[]>([]);
  const [session, setSession] = useState<InterviewSession | null>(null);
  const [toolEvents, setToolEvents] = useState<ToolEvent[]>([]);
  const [elapsed, setElapsed] = useState(0);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const locked = detail.stages.interview === "locked";

  useEffect(() => {
    api.config().then(setConfig, () => {});
  }, [api]);

  // Mount the employee's own avatar from the studio SDK.
  useEffect(() => {
    if (!e.avatarPin || !config || !canvasRef.current) return;
    let disposed = false;
    setAvatarState("loading");
    (async () => {
      try {
        const sdk = await import(/* @vite-ignore */ `${config.studioUrl}/avatar-sdk.mjs`);
        const a: StudioAvatar = await sdk.mountAvatar({ canvas: canvasRef.current, manifestURL: e.avatarPin!.manifestUrl, onStatus: (s: string) => setStatus(s) });
        if (disposed) return a.dispose();
        avatarRef.current = a;
        setAvatarState("ready");
      } catch {
        if (!disposed) setAvatarState("failed");
      }
    })();
    return () => {
      disposed = true;
      avatarRef.current?.dispose();
      avatarRef.current = null;
    };
  }, [e.avatarPin?.manifestUrl, config?.studioUrl]);

  const refreshSession = useCallback(async (sid: string) => {
    const d = await api.interview(sid);
    setSession(d.session);
    setToolEvents(d.toolEvents);
  }, [api]);

  const end = useCallback(async () => {
    const cur = liveRef.current;
    if (!cur) return;
    liveRef.current = null;
    avatarRef.current?.disconnect();
    setLive(null);
    try {
      await turnQueue.drain();
      await api.endInterview(cur.session.id);
      await refreshSession(cur.session.id);
    } catch (err) {
      setError(errText(err));
    }
    await reload();
  }, [api, reload, refreshSession, turnQueue]);

  // End the session if the screen goes away mid-interview.
  useEffect(() => () => void end(), [end]);

  // Session clock, with an automatic stop at the limit.
  useEffect(() => {
    if (!live) return;
    const started = Date.now();
    setElapsed(0);
    const t = setInterval(() => {
      const s = Math.floor((Date.now() - started) / 1000);
      setElapsed(s);
      if (s >= live.maxMinutes * 60) void end();
    }, 1000);
    return () => clearInterval(t);
  }, [live, end]);

  const addTurn = (sid: string, role: "user" | "avatar", text: string) => {
    const t = text.trim();
    if (!t) return;
    setTurns((xs) => [...xs, { role, text: t }]);
    turnQueue.push(() => api.postTurn(sid, role, t));
  };

  const start = async () => {
    setBusy(true);
    setError(undefined);
    setTurns([]);
    try {
      const started = await api.startInterview(e.id);
      liveRef.current = started;
      setLive(started);
      setSession(started.session);
      setToolEvents([]);
      const sid = started.session.id;
      const a = avatarRef.current;
      if (!a) throw new Error("The avatar is not loaded, so the voice interview cannot start.");
      await a.connectOpenAI({
        ephemeralToken: started.clientSecret,
        model: started.model,
        onToolCall: async (name, args) => {
          const r = await api.runTool(sid, name, args);
          void refreshSession(sid);
          return r.output;
        },
        onEvent: (m) => {
          if (m.type === "session.created") a.channel?.send(JSON.stringify({ type: "response.create" }));
          if (m.type === "conversation.item.input_audio_transcription.completed") addTurn(sid, "user", m.transcript ?? "");
          if (m.type === "response.output_audio_transcript.done") addTurn(sid, "avatar", m.transcript ?? "");
        },
      });
    } catch (err) {
      setError(errText(err));
      await end();
    } finally {
      setBusy(false);
    }
  };

  const sendTyped = () => {
    const a = avatarRef.current;
    const text = typed.trim();
    if (!live || !a?.channel || !text) return;
    a.channel.send(JSON.stringify({ type: "conversation.item.create", item: { type: "message", role: "user", content: [{ type: "input_text", text }] } }));
    a.channel.send(JSON.stringify({ type: "response.create" }));
    addTurn(live.session.id, "user", text);
    setTyped("");
  };

  const topics = config?.coverageTopics ?? [];
  const coverage = session?.coverage ?? Object.assign({}, ...detail.sessions.map((s) => s.coverage));
  const coveredCount = topics.filter((t) => (coverage[t.id]?.confidence ?? 0) >= 0.7).length;
  const pastSessions = detail.sessions.filter((s) => s.status !== "live").slice().reverse();
  const mm = (n: number) => `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;

  return (
    <div className="grid gap-4">
      <SectionTitle
        title="Interview"
        description={`${e.name} interviews you about the job, by voice. It can search the web and read your uploaded material to ask sharper follow-up questions. Run as many sessions as you need; each picks up where the last one stopped.`}
        action={
          <Button variant="outline" disabled={detail.stages.distill === "locked" || Boolean(live)} onClick={() => go(`/employees/${e.id}/distill`)}>
            Continue to distill
          </Button>
        }
      />
      {locked && <p className="text-sm text-muted-foreground">Pin an avatar and set the job title first.</p>}
      <ErrorNote error={error} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card>
          <CardContent className="grid gap-3">
            <div className="relative aspect-[4/5] max-h-[460px] w-full overflow-hidden rounded-lg bg-muted">
              <canvas ref={canvasRef} className="size-full" aria-label={`${e.name}, 3D avatar`} />
              {avatarState !== "ready" && (
                <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-muted-foreground">
                  {avatarState === "loading" ? "Loading avatar…" : "The avatar could not be loaded from the studio. Check that the studio is reachable and the release is still published."}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                {live ? <StatusDot tone="success" label="Live" pulse /> : <StatusDot tone="neutral" label="Not connected" />}
                {live && (
                  <span className="font-mono text-sm text-muted-foreground">
                    {mm(elapsed)} / {live.maxMinutes}:00
                  </span>
                )}
                {status && <span className="text-xs text-muted-foreground">{status}</span>}
              </div>
              {live ? (
                <Button variant="destructive" onClick={() => void end()}>
                  End interview
                </Button>
              ) : (
                <Button disabled={busy || locked || avatarState !== "ready"} onClick={() => void start()}>
                  {detail.sessions.some((s) => s.status === "ended") ? "Start another session" : "Start interview"}
                </Button>
              )}
            </div>
            {live && (
              <form
                className="flex gap-2"
                onSubmit={(x) => {
                  x.preventDefault();
                  sendTyped();
                }}
              >
                <Input aria-label="Type instead of speaking" placeholder="Type instead of speaking…" value={typed} onChange={(x) => setTyped(x.target.value)} />
                <Button type="submit" variant="secondary" disabled={!typed.trim()}>
                  Send
                </Button>
              </form>
            )}
            {(live || turns.length > 0) && (
              <div className="max-h-72 overflow-y-auto rounded-lg border p-3 text-sm" aria-label="Transcript">
                {turns.length === 0 && <p className="text-muted-foreground">Listening…</p>}
                {turns.map((t, i) => (
                  <p key={i} className="mb-2">
                    <span className="font-medium">{t.role === "user" ? "You" : e.name}: </span>
                    {t.text}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid content-start gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Coverage</CardTitle>
              <CardDescription>
                {coveredCount} of {topics.length} topics understood
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-2">
              <Progress value={topics.length ? (coveredCount / topics.length) * 100 : 0} aria-label="Coverage" />
              <ul className="grid gap-2">
                {topics.map((t) => {
                  const c = coverage[t.id];
                  const tone = !c ? "neutral" : c.confidence >= 0.7 ? "success" : "warning";
                  const label = !c ? "Not yet" : c.confidence >= 0.7 ? "Covered" : "Partly";
                  return (
                    <li key={t.id} className="text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span>{t.label}</span>
                        <StatusDot tone={tone} label={label} className="text-xs" />
                      </div>
                      {c?.note && <p className="text-xs text-muted-foreground">{c.note}</p>}
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>

          {toolEvents.filter((t) => t.tool !== "mark_covered").length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Lookups</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 text-sm">
                {toolEvents
                  .filter((t) => t.tool !== "mark_covered")
                  .map((t) => (
                    <div key={t.id}>
                      <span className="font-medium">{t.tool === "web_search" ? "Web" : "Your material"}:</span> {String((t.args as any)?.query ?? "")}
                      {!t.ok && <span className="text-destructive"> (failed)</span>}
                    </div>
                  ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {pastSessions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Earlier sessions</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            {pastSessions.map((s) => (
              <div key={s.id} className="text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{formatWhen(s.startedAt)}</span>
                  <StatusDot tone={s.status === "ended" ? "success" : "neutral"} label={s.status === "ended" ? "Ended" : "Abandoned"} className="text-xs" />
                </div>
                <p className="text-muted-foreground">{s.summary ?? (s.status === "ended" ? "Summarising…" : "No summary.")}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
